import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { ArrowLeft, Eye } from "lucide-react"
import { mlSheetService } from "../../services/mlSheetService"
import { shipmentService } from "../../services/shipmentService"
import { useAuth } from "../../context/AuthContext"
import Alert from "../common/Alert"
import {
  PageLayout,
  Button,
  DataTable,
  DetailCard,
  FormField,
  StatusBadge,
} from "../common/index.js"

const StatusUpdate = () => {
  const { mlSheetId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [mlSheet, setMLSheet] = useState(null)
  const [shipments, setShipments] = useState([])
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)

  const [selectedShipments, setSelectedShipments] = useState(new Set())
  const [selectAll, setSelectAll] = useState(false)

  const [selectedStatus, setSelectedStatus] = useState("Received")
  const [remarks, setRemarks] = useState("Status updated via bulk update")

  const [deliveryReceiver, setDeliveryReceiver] = useState("")
  const [deliveryReceiverImage, setDeliveryReceiverImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)

  const ALLOWED_STATUSES = ["Received", "Delivered"]

  useEffect(() => {
    fetchData()
  }, [mlSheetId])

  const fetchData = async () => {
    try {
      setLoading(true)

      const mlResponse = await mlSheetService.getMLSheet(mlSheetId)
      const sheetData = mlResponse.data.ml_sheet || mlResponse.data.data
      const shipmentsData = mlResponse.data.shipments || []

      setMLSheet(sheetData)
      setShipments(Array.isArray(shipmentsData) ? shipmentsData : [])
    } catch (error) {
      console.error("Error fetching data:", error)
      Alert.error("Failed to load data")
      navigate("/ml-sheets")
    } finally {
      setLoading(false)
    }
  }

  const canUpdateStatus = () => {
    if (!user) return false

    const allowedRoles = ["System Admin", "Admin", "Accountant"]

    if (allowedRoles.includes(user.role)) {
      return true
    }

    if (user.role === "Agent" && mlSheet?.access_users) {
      const hasAccess = mlSheet.access_users.some((accessUser) => {
        if (typeof accessUser === "string") {
          return accessUser === user.user_id
        }
        return (
          accessUser.user_id === user.user_id ||
          accessUser.id === user.user_id ||
          accessUser === user.user_id
        )
      })

      return hasAccess
    }

    return false
  }

  const handleSelectAll = (e) => {
    const checked = e.target.checked
    setSelectAll(checked)

    if (checked) {
      setSelectedShipments(new Set(shipments.map((s) => s.shipment_id)))
    } else {
      setSelectedShipments(new Set())
    }
  }

  const handleSelectShipment = (shipmentId) => {
    const newSelected = new Set(selectedShipments)
    if (newSelected.has(shipmentId)) {
      newSelected.delete(shipmentId)
    } else {
      newSelected.add(shipmentId)
    }
    setSelectedShipments(newSelected)
    setSelectAll(newSelected.size === shipments.length)
  }

  const handleImageSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"]
      if (!allowedTypes.includes(file.type)) {
        Alert.error("Invalid file type. Only JPG, PNG, GIF, and WebP are allowed.")
        return
      }

      if (file.size > 2097152) {
        Alert.error("File size too large. Maximum size is 2MB.")
        return
      }

      setDeliveryReceiverImage(file)

      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveImage = () => {
    setDeliveryReceiverImage(null)
    setImagePreview(null)
    const fileInput = document.getElementById("delivery-receiver-image")
    if (fileInput) {
      fileInput.value = ""
    }
  }

  const handleBulkStatusUpdate = async () => {
    if (selectedShipments.size === 0) {
      Alert.error("Please select at least one shipment")
      return
    }

    if (!selectedStatus) {
      Alert.error("Please select a status")
      return
    }

    try {
      setUpdating(true)

      const shipmentIds = Array.from(selectedShipments)

      const response = await shipmentService.bulkUpdateStatus(
        shipmentIds,
        selectedStatus,
        remarks,
        deliveryReceiver,
        deliveryReceiverImage
      )

      if (response.data?.success) {
        Alert.success(
          `Successfully updated ${response.data.updated_count} shipment(s) to ${selectedStatus}`
        )

        setSelectedShipments(new Set())
        setSelectAll(false)
        setDeliveryReceiver("")
        setDeliveryReceiverImage(null)
        setImagePreview(null)

        await fetchData()
      } else {
        Alert.error(response.data?.error || "Failed to update shipments")
      }
    } catch (error) {
      console.error("Error updating shipments:", error)
      Alert.error(error.response?.data?.error || "Failed to update shipments")
    } finally {
      setUpdating(false)
    }
  }

  const getShipmentStatusVariant = (status) => {
    const statusMap = {
      booked: "info",
      dispatched: "warning",
      received: "warning",
      delivered: "success",
      rejected: "danger",
      returned: "warning",
    }
    return statusMap[status?.toLowerCase()] || "default"
  }

  if (loading) {
    return (
      <PageLayout title="Bulk Status Update" maxWidth="max-w-7xl">
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-gray-600">Loading Status Update...</p>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (!canUpdateStatus()) {
    return (
      <PageLayout title="Bulk Status Update" maxWidth="max-w-7xl">
        <DetailCard>
          <p className="text-red-600 text-lg font-semibold mb-4 text-center">Access Denied</p>
          <p className="text-gray-600 mb-6 text-center">
            You don't have permission to update shipment statuses.
          </p>
          <div className="flex justify-center">
            <Button variant="secondary" onClick={() => navigate(`/ml-sheets/view/${mlSheetId}`)}>
              Back to ML Sheet
            </Button>
          </div>
        </DetailCard>
      </PageLayout>
    )
  }

  const shipmentColumns = [
    {
      key: "select",
      label: (
        <input
          type="checkbox"
          checked={selectAll}
          onChange={handleSelectAll}
          className="w-4 h-4 rounded border border-gray-400 cursor-pointer accent-blue-600"
        />
      ),
      width: "48px",
      render: (shipment) => (
        <input
          type="checkbox"
          checked={selectedShipments.has(shipment.shipment_id)}
          onChange={() => handleSelectShipment(shipment.shipment_id)}
          className="w-4 h-4 rounded border border-gray-400 cursor-pointer accent-blue-600"
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      key: "shipment_id",
      label: "Shipment ID",
      render: (shipment) => (
        <span className="font-semibold text-secondary">{shipment.shipment_id}</span>
      ),
    },
    {
      key: "sender",
      label: "Sender",
      render: (shipment) => (
        <>
          <div className="font-semibold text-gray-800 text-sm">{shipment.sender_name}</div>
          <div className="text-xs text-gray-600">{shipment.sender_contact}</div>
        </>
      ),
    },
    {
      key: "receiver",
      label: "Receiver",
      render: (shipment) => (
        <>
          <div className="font-semibold text-gray-800 text-sm">{shipment.receiver_name}</div>
          <div className="text-xs text-gray-600">{shipment.receiver_contact}</div>
        </>
      ),
    },
    {
      key: "amount",
      label: "Amount",
      render: (shipment) => `₹${shipment.total_amount?.toFixed(2) || "0.00"}`,
    },
    {
      key: "status",
      label: "Current Status",
      render: (shipment) => (
        <StatusBadge status={shipment.status} variant={getShipmentStatusVariant(shipment.status)} />
      ),
    },
  ]

  const getShipmentActions = (shipment) => [
    {
      label: "View",
      icon: Eye,
      onClick: () => navigate(`/shipments/view/${shipment.shipment_id}`),
    },
  ]

  const shipmentMobileCardRender = (shipment) => (
    <div
      className={`transition-colors ${
        selectedShipments.has(shipment.shipment_id)
          ? "bg-blue-100 border-2 border-blue-400 rounded-sm p-2 -m-2"
          : ""
      }`}
      onClick={() => handleSelectShipment(shipment.shipment_id)}
    >
      <div className="flex items-start gap-3 mb-3">
        <input
          type="checkbox"
          checked={selectedShipments.has(shipment.shipment_id)}
          onChange={(e) => {
            e.stopPropagation()
            handleSelectShipment(shipment.shipment_id)
          }}
          className="w-5 h-5 rounded border-2 border-gray-400 cursor-pointer accent-blue-600 mt-1 flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-blue-600 text-lg break-words">
            {shipment.shipment_id}
          </div>
          <div className="flex justify-between items-center mt-2 flex-wrap gap-2">
            <StatusBadge
              status={shipment.status}
              variant={getShipmentStatusVariant(shipment.status)}
            />
            <span className="font-bold text-gray-800 text-sm">
              ₹{shipment.total_amount?.toFixed(2) || "0.00"}
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-2 mb-3">
        <div className="bg-gray-50 rounded p-2">
          <div className="text-xs text-gray-600 font-semibold">Sender</div>
          <div className="font-semibold text-gray-800 text-sm">{shipment.sender_name}</div>
          <div className="text-xs text-gray-600">{shipment.sender_contact}</div>
        </div>

        <div className="bg-gray-50 rounded p-2">
          <div className="text-xs text-gray-600 font-semibold">Receiver</div>
          <div className="font-semibold text-gray-800 text-sm">{shipment.receiver_name}</div>
          <div className="text-xs text-gray-600">{shipment.receiver_contact}</div>
        </div>
      </div>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          navigate(`/shipments/view/${shipment.shipment_id}`)
        }}
        className="w-full text-center text-brand hover:text-brand font-semibold text-sm py-2 hover:bg-brand-light rounded"
      >
        View Details →
      </button>
    </div>
  )

  return (
    <PageLayout
      title="Bulk Status Update"
      maxWidth="max-w-7xl"
      actions={
        <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate(`/ml-sheets/view/${mlSheetId}`)}>
          Back
        </Button>
      }
    >
      <DetailCard title="Update Status">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <FormField label="Select Status" required>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm font-medium"
            >
              {ALLOWED_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Only "Received" and "Delivered" statuses are allowed
            </p>
          </FormField>

          <FormField label="Selected Shipments">
            <div className="flex items-center justify-center px-4 py-3 border rounded-sm">
              <div className="text-sm font-semibold text-secondary">{selectedShipments.size}</div>
              <div className="text-sm text-secondary font-semibold ml-2">/ {shipments.length}</div>
            </div>
          </FormField>

          <FormField label="Remarks (Optional)">
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Add remarks for this status update..."
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-sm focus:outline-none focus:border-brand resize-none"
              rows="1"
            />
          </FormField>
        </div>

        {selectedStatus === "Delivered" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 p-4 bg-blue-50 rounded-sm border border-blue-200">
            <div className="md:col-span-2">
              <h4 className="text-sm font-semibold text-blue-800 mb-3">
                Delivery Information (Required for Delivered Status)
              </h4>
            </div>

            <FormField label="Receiver Detail">
              <input
                type="text"
                value={deliveryReceiver}
                onChange={(e) => setDeliveryReceiver(e.target.value)}
                placeholder="Enter detail of person who received the delivery"
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-sm focus:outline-none focus:border-brand"
                maxLength={250}
              />
              <p className="text-xs text-gray-500 mt-1">
                Detail of the person who received the delivery
              </p>
            </FormField>

            <FormField label="Receiver Image">
              <input
                type="file"
                id="delivery-receiver-image"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                capture="environment"
                onChange={handleImageSelect}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-sm focus:outline-none focus:border-brand file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p className="text-xs text-gray-500 mt-1">Photo of person who received (Max 2MB)</p>
            </FormField>

            {imagePreview && (
              <div className="md:col-span-2">
                <FormField label="Image Preview">
                  <div className="relative inline-block">
                    <img
                      src={imagePreview}
                      alt="Delivery Receiver Preview"
                      className="max-w-full h-48 object-cover rounded-sm border-2 border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      title="Remove image"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </FormField>
              </div>
            )}
          </div>
        )}

        <Button
          onClick={handleBulkStatusUpdate}
          loading={updating}
          disabled={updating || selectedShipments.size === 0}
        >
          {updating ? "Updating..." : `Update ${selectedShipments.size} Shipment(s)`}
        </Button>
      </DetailCard>

      <DetailCard title={`Shipments (${shipments.length})`}>
        <div className="flex items-center gap-3 mb-4">
          <input
            type="checkbox"
            checked={selectAll}
            onChange={handleSelectAll}
            className="w-5 h-5 rounded border-2 border-gray-400 cursor-pointer accent-blue-600"
          />
          <span className="text-sm text-secondary">Select all shipments</span>
        </div>

        <DataTable
          columns={shipmentColumns}
          data={shipments}
          rowKey="id"
          actions={getShipmentActions}
          mobileCardRender={shipmentMobileCardRender}
          emptyMessage="No shipments in this ML Sheet"
        />
      </DetailCard>
    </PageLayout>
  )
}

export default StatusUpdate
